import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/sv";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Linking } from "react-native";
import { Spacer } from "~/components/Spacer";
import { Calendar } from "~/components/V2/Calendar";
import { Pressable } from "~/components/V2/Pressable";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { TimeslotList } from "~/components/V2/TimeslotList";
import { View } from "~/components/V2/View";
import { useClinicPhoneNumber } from "~/hooks/useClinicPhoneNumber";
import { useResponsive } from "~/hooks/useResponsive";
import type { TimeslotInstance } from "~/mobx/entities/timeslots/Timeslot";
import { useStore } from "~/mobx/utils/useStore";
import { assert } from "~/utils/assert";
import { getPhoneNumberWithCountryCode } from "~/utils/getPhoneNumberWithCountryCode";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { useProgressBarStep } from "./useProgressBarStep";
import { withBookingData } from "./withBookingData";

// TODO: set the correct timezone for the clinic when we add multiple countries
// or extract the timezone to env variables
const clinicTimezone = "Europe/Stockholm";

const SelectDateAndTimeScreen = observer(function SelectDateAndTimeScreen() {
  const store = useStore();
  const r = useResponsive();
  const { clinicPhoneNumber, clinicPhoneNumberCountryCode } =
    useClinicPhoneNumber();

  useProgressBarStep(5);

  const [startDay, setStartDay] = useState(dayjs().tz(clinicTimezone));
  const [selectedDay, setSelectedDay] = useState<null | dayjs.Dayjs>(null);

  const clinicId = store.bookingStore.clinicId;
  const reasonId = store.bookingStore.reasonId;

  // if the practitionerId has been preselected
  const practitionerId = store.bookingStore.practitionerId;

  const query = useQuery({
    queryKey: ["timeslots", { selectedDay, clinicId, reasonId }],
    queryFn: async () => {
      assert(selectedDay, "selectedDay is required");
      const timeslots = store.timeslotsStore.getTimeslotList({
        from: selectedDay,
        to: selectedDay.endOf("day"),
      });
      return timeslots;
    },
    enabled: !!selectedDay,
  });

  const filterByPractitionerId = (timeslot: TimeslotInstance) =>
    practitionerId ? timeslot.practitioner.id === practitionerId : true;

  const morningTimeslots = query.data
    ?.filter((timeslot) => {
      return (
        dayjs
          .utc(timeslot.startUtc, "YYYY-MM-DDTHH:mm:ss[Z]")
          .tz(clinicTimezone)
          .hour() < 12
      );
    })
    .filter(filterByPractitionerId);

  const afternoonTimeslots = query.data
    ?.filter((timeslot) => {
      return (
        dayjs
          .utc(timeslot.startUtc, "YYYY-MM-DDTHH:mm:ss[Z]")
          .tz(clinicTimezone)
          .hour() >= 12
      );
    })
    .filter(filterByPractitionerId);

  return (
    <View colorWhite flex>
      {/* Title */}
      <View paddingVertical40 alignItemsCenter>
        <BrandText sizeH1 weightExtraBold colorBlack>
          <FormattedMessage
            id="yKRUFV"
            defaultMessage="Select date and time"
            description="Title on the select date and time screen in booking flow"
          />
        </BrandText>
      </View>

      <View
        style={{
          paddingHorizontal: r(0, 100, 100),
        }}
      >
        <Calendar
          clinicTimezone={clinicTimezone}
          startDay={startDay}
          selectedDay={selectedDay}
          onPressSelectDay={setSelectedDay}
          onPressNextWeek={() => {
            setStartDay((prev) => prev.add(1, "week"));
          }}
          onPressPreviousWeek={() => {
            setStartDay((prev) => prev.subtract(1, "week"));
          }}
          setStartDay={setStartDay}
        />

        {/* Timeslots  */}
        <>
          {query.isLoading ? (
            <View centerContent style={{ paddingTop: 90 }}>
              <SpinnerV2 />
            </View>
          ) : query.data?.length === 0 ? (
            <View centerContent paddingVertical24>
              <BodyText sizeLarge weightRegular colorGrey>
                <FormattedMessage
                  id="uH7eR5"
                  defaultMessage="No available times, choose another date"
                  description="No available timeslots message"
                />
              </BodyText>
            </View>
          ) : query.data !== undefined ? (
            <View
              paddingHorizontal16
              style={{ borderBottomWidth: 1, borderColor: "#e6e6e6d9" }}
            >
              <Spacer extraLarge />

              <TimeslotList
                data={morningTimeslots ?? []}
                title={
                  <FormattedMessage
                    id="1UG0k5"
                    defaultMessage="Morning"
                    description="Morning timeslots title"
                  />
                }
                clinicTimezone={clinicTimezone}
              />

              <Spacer extraLarge />

              <TimeslotList
                data={afternoonTimeslots ?? []}
                title={
                  <FormattedMessage
                    id="UNgv4x"
                    defaultMessage="Afternoon"
                    description="Afternoon timeslots title"
                  />
                }
                clinicTimezone={clinicTimezone}
              />
              <Spacer extraExtraLarge />
            </View>
          ) : null}
        </>
        <Spacer extraExtraLarge />

        {!!clinicPhoneNumber && !!clinicPhoneNumberCountryCode && (
          <View
            centerContent
            style={{
              paddingHorizontal: r(16, 0, 0),
            }}
          >
            <BodyText alignCenter sizeExtraLarge>
              <FormattedMessage
                id="TaZfZ3"
                defaultMessage="If you cannot find a bookable time for your matter, please contact us on <phoneNumber>{number}</phoneNumber>."
                description="Contact us message"
                values={{
                  number: clinicPhoneNumber,
                  phoneNumber: (chunk) => {
                    return (
                      <Pressable
                        onPress={() => {
                          // Log the event to the booking data layer
                          bookingDataLayerPush({
                            event: "call_clinic_phone_web_app",
                            ga_action: "call_clinic_phone",
                            ga_label: "SelectDateAndTimeScreen",
                          });

                          Linking.openURL(
                            `tel:${getPhoneNumberWithCountryCode(clinicPhoneNumberCountryCode, clinicPhoneNumber)}`,
                          );
                        }}
                      >
                        <BodyText sizeExtraLarge colorPrimary underline>
                          {chunk}
                        </BodyText>
                      </Pressable>
                    );
                  },
                }}
              />
            </BodyText>
          </View>
        )}

        <Spacer extraExtraLarge />
        <Spacer extraExtraLarge />
        <Spacer large />
      </View>
    </View>
  );
});

// If any required data from earlier in the booking process is missing, redirect to the first step
export const SelectDateAndTimeScreenWithBookingData = withBookingData(
  SelectDateAndTimeScreen,
  ["clinicId", "patientId", "reasonId"],
);
